import i18n from "i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import { initReactI18next } from "react-i18next";

import { DEFAULT_LANGUAGE, SUPPORTED_LANGUAGE_CODES, normalizeLanguage } from "./languages";
import { LANGUAGE_STORAGE_KEY } from "./storage";

/**
 * Locale resources are split one JSON file per section per language
 * (`locales/<lng>/<section>.json`) and deep-merged here into i18next's single
 * default `translation` namespace. Keys are unchanged — still `section.key` —
 * so no call site cares about the layout.
 *
 * The split exists so that parallel translation PRs each own their own section
 * files and never collide in one giant JSON.
 */
const modules = import.meta.glob<Record<string, unknown>>("./locales/*/*.json", {
  eager: true,
  import: "default",
});

function buildResources(): Record<string, { translation: Record<string, unknown> }> {
  const resources: Record<string, { translation: Record<string, unknown> }> = {};
  for (const [path, body] of Object.entries(modules)) {
    // ./locales/<lng>/<section>.json
    const match = path.match(/\.\/locales\/([^/]+)\/([^/]+)\.json$/);
    if (!match) continue;
    const [, lng, section] = match;
    resources[lng] ??= { translation: {} };
    resources[lng].translation[section] = body;
  }
  return resources;
}

export const resources = buildResources();

/**
 * Language resolution order, highest priority first:
 *
 * 1. `localStorage` (`coffee-language`) — what this user last chose. Read
 *    synchronously at init so a returning user never sees a flash of English.
 * 2. `navigator` — the browser's languages, for a first-time user who has
 *    never touched the picker. Unshipped languages fall through.
 * 3. `fallbackLng` — English.
 *
 * The signed-in user's saved backend preference outranks all three, but it is
 * only known after the first GraphQL round-trip; `useLanguage` applies it once
 * it loads (see `src/lib/i18n/useLanguage.ts`).
 *
 * `caches: []` because we write `localStorage` ourselves, in one place, in the
 * JSON-quoted shape the legacy `useStickyState` readers expect — letting the
 * detector also write would put a second, bare-string format under the same key.
 */
i18n.use(LanguageDetector).use(initReactI18next).init({
  resources,
  fallbackLng: DEFAULT_LANGUAGE,
  supportedLngs: [...SUPPORTED_LANGUAGE_CODES],
  // `pt-BR` and `zh-Hans-CN` resolve to the `pt` / `zh` catalogs we ship.
  load: "languageOnly",
  detection: {
    order: ["localStorage", "navigator"],
    lookupLocalStorage: LANGUAGE_STORAGE_KEY,
    caches: [],
    // Runs on every raw candidate before i18next matches it against
    // `supportedLngs`. It strips the quotes off the JSON-quoted value the
    // legacy picker wrote, folds regional tags onto the base code, and maps
    // anything we don't ship to "" so the next candidate — ultimately
    // `fallbackLng` — wins. (The option is typed `string => string`, so "" is
    // how "no match" is spelled; i18next skips it as unsupported.)
    convertDetectedLanguage: (lng: string) => normalizeLanguage(lng) ?? "",
  },
  interpolation: { escapeValue: false },
  // i18next otherwise console.info's a Locize advert on init, which the test
  // suite now prints once per file since tests run the real instance.
  showSupportNotice: false,
});

export default i18n;
